import { useState, useEffect, useCallback } from 'react';
import supabase from '../supabase/supabase';

export const useFollows = (fanId, artistId) => {
  const [isFollowing, setIsFollowing] = useState(false);
  const [followCount, setFollowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!artistId) return;
    setLoading(true);
    try {
      const [countRes, followRes] = await Promise.all([
        supabase.rpc('get_artist_follow_count', { p_artist_id: artistId }),
        fanId
          ? supabase.rpc('get_follow_status', { p_fan_id: fanId, p_artist_id: artistId })
          : Promise.resolve({ data: false }),
      ]);
      setFollowCount(countRes.data ?? 0);
      setIsFollowing(followRes.data ?? false);
    } catch (err) {
      console.error('[useFollows] fetchStatus error:', err);
    } finally {
      setLoading(false);
    }
  }, [fanId, artistId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const toggleFollow = async () => {
    if (!fanId || !artistId || toggling) return;
    setToggling(true);
    // Optimistic update
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);
    setFollowCount(c => wasFollowing ? Math.max(0, c - 1) : c + 1);
    try {
      const { error } = await supabase.rpc('toggle_artist_follow', {
        p_fan_id: fanId,
        p_artist_id: artistId,
      });
      if (error) throw error;
    } catch (err) {
      // Revert optimistic update on error
      setIsFollowing(wasFollowing);
      setFollowCount(c => wasFollowing ? c + 1 : Math.max(0, c - 1));
      console.error('[useFollows] toggleFollow error:', err);
    } finally {
      setToggling(false);
    }
  };

  return { isFollowing, followCount, loading, toggling, toggleFollow, refetch: fetchStatus };
};

export const useFanFollows = (fanId) => {
  const [follows, setFollows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchFollows = useCallback(async () => {
    if (!fanId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('d2f_follows')
        .select(`
          id, created_at,
          artist:d2f_artists(id, name, slug, avatar_image, genre, verified)
        `)
        .eq('fan_id', fanId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setFollows(data || []);
    } catch (err) {
      console.error('[useFanFollows] error:', err);
    } finally {
      setLoading(false);
    }
  }, [fanId]);

  useEffect(() => { fetchFollows(); }, [fetchFollows]);

  return { follows, loading, refetch: fetchFollows };
};